import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, Pagination } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHistory } from '../../../store/strategistSlice';
import { COLORS } from '../../../shared/colors';
import TaskGeneratorHistoryItem from './TaskGeneratorHistoryItem';

const TaskGeneratorHistoryTab = ({ selectedProjects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [lastFetchParams, setLastFetchParams] = useState(null);
  const limit = 10;
  const { history, loading, error } = useSelector((state) => state.strategist);
  const { data: historyData = [], totalPages = 0 } = history || {};

  useEffect(() => {
    const params = {
      type: 'taskgeneration',
      page,
      limit
    };
    
    if (selectedProjects && selectedProjects.length > 0) {
      params.projects = selectedProjects;
    }
    
    const paramsString = JSON.stringify(params);
    if (paramsString !== lastFetchParams) {
      setLastFetchParams(paramsString);
      dispatch(fetchHistory(params));
    }
  }, [selectedProjects, dispatch, page, limit, lastFetchParams]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  if (loading.fetchHistory) {
    return (
      <Box className="history-loading">
        <CircularProgress />
        <Typography variant="body2" className="history-loading-text">
          {t('agent.history.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="history-error">
        {error}
      </Alert>
    );
  }

  if (!historyData || historyData.length === 0) {
    return (
      <Box className="history-empty">
        <Typography variant="h6" className="history-empty-title">
          {t('agent.history.empty.title')}
        </Typography>
        <Typography variant="body2" className="history-empty-description">
          Your task generator history will appear here once you start generating tasks.
        </Typography>
      </Box>
    );
  }

  // Group flattened items by project name
  const groupedByProject = historyData.reduce((acc, item) => {
    const projectName = item.project_name || 'Unknown Project';
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(item);
    return acc;
  }, {});

  return (
    <Box className="task-generator-history-tab">
      {Object.entries(groupedByProject).map(([projectName, items]) => (
        <Accordion
          key={projectName}
          defaultExpanded
          sx={{
            mb: 2,
            border: `1px solid ${COLORS.gray[200]}`,
            '&:before': { display: 'none' },
            borderRadius: '10px !important',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08) !important',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: COLORS.blue[50],
              borderBottom: `1px solid ${COLORS.gray[200]}`,
              '&:hover': { bgcolor: COLORS.blue[100] },
              '& .MuiAccordionSummary-content': { alignItems: 'center' },
            }}
          >
            <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {projectName}
              <Box
                sx={{
                  background: COLORS.primary,
                  color: 'white',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {items.length}
              </Box>
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 2, bgcolor: COLORS.gray[25] || '#fafafa' }}>
            <Box className="history-items-list">
              {items.map((item) => (
                <TaskGeneratorHistoryItem
                  key={item.ticket_id || `${item.project_id}-${item.ticket_number}`}
                  item={item}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box className="history-pagination-container">
        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          disabled={!totalPages || totalPages <= 1}
          slotProps={{
            previous: { title: t('pagination.previous') },
            next: { title: t('pagination.next') },
            first: { title: t('pagination.first') },
            last: { title: t('pagination.last') }
          }}
        />
      </Box>
    </Box>
  );
};

export default TaskGeneratorHistoryTab;
